import { type ConnectionStatus, Network } from '@capacitor/network'

/**
 * Non-React online/offline signal.
 *
 * The urql auth exchange and the GraphQL `onError` handler run OUTSIDE React, so
 * they can't read the `useNetwork` hook. This module keeps a single live boolean
 * sourced from `@capacitor/network` (with the webview online/offline events as a
 * fallback, mirroring `useNetwork`) so non-React code can ask `getIsOnline()`.
 *
 * We deliberately rely on the Capacitor signal rather than `navigator.onLine`,
 * which is unreliable in the iOS WKWebView (it can report online with no real
 * connectivity). The listeners are registered once, on first import.
 */

let online = navigator.onLine

Network.getStatus()
  .then((status) => {
    online = status.connected
  })
  .catch(() => {
    // Keep the navigator.onLine seed if Capacitor can't report status.
  })

Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
  online = status.connected
})

// Fallback: the webview's own events. @capacitor/network can miss the reconnect
// transition (notably on the iOS simulator).
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    online = true
  })
  window.addEventListener('offline', () => {
    online = false
  })
}

/** Current connectivity, for code that runs outside React. */
export const getIsOnline = (): boolean => online
